import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { acceptCoachInvitation } from '../../utils/invitationUtils';
import { toast } from 'react-hot-toast';
import Logo from '../layout/Logo';
import SocialLogin from './SocialLogin';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<any>(null);
  const [role, setRole] = useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [teamName, setTeamName] = useState<string>('');
  const [teamLogo, setTeamLogo] = useState<string>('');
  const [teamColors, setTeamColors] = useState<any>(null);
  const [teamContext, setTeamContext] = useState<any>(null);
  const [success, setSuccess] = useState<string>('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Get invitation ID from URL parameters
  const invitationId = searchParams.get('invitation');

  // Load invitation details if invitation ID is provided
  useEffect(() => {
    if (invitationId) {
      loadInvitation(invitationId);
    }
  }, [invitationId]);

  const loadInvitation = async (invitationId: string) => {
    try {
      console.log('Loading invitation with ID:', invitationId);
      
      // First get the invitation details with team information
      // Use a join to get team data in a single query, which should work better with RLS
      const { data: invitationWithTeam, error: invitationError } = await supabase
        .from('coach_invitations')
        .select(`
          id,
          team_id,
          email,
          role,
          position,
          status,
          invited_by,
          created_at,
          accepted_at,
          teams!inner(
            id,
            name,
            sport,
            age_level,
            experience_level
          )
        `)
        .eq('id', invitationId)
        .eq('status', 'pending')
        .maybeSingle();

      if (invitationError) {
        console.error('Error loading invitation:', invitationError);
        setError('Failed to load invitation. Please try again.');
        return;
      }

      if (!invitationWithTeam) {
        console.error('No invitation found with ID:', invitationId);
        setError('Invitation not found or has expired.');
        return;
      }

      console.log('Found invitation with team:', invitationWithTeam);

      // Extract the invitation and team data
      const { teams, ...invitation } = invitationWithTeam;
      const team = teams;

      if (!team) {
        console.error('No team found for invitation:', invitation);
        setError('Team not found. Please contact support.');
        return;
      }

      console.log('Found team:', team);

      // Combine the data and set it once
      const fullInvitation = {
        ...invitation,
        team
      };

      setInvitation(fullInvitation);
      setEmail(invitation.email);
      setRole(invitation.role);
      setTeamId(invitation.team_id);
      setTeamName(team.name);
      setTeamLogo(''); // logo_url doesn't exist in database
      setTeamColors(null); // colors don't exist in database
      setTeamContext(team);
      setSuccess('You\'re invited to join ' + team.name);
    } catch (error) {
      console.error('Error in loadInvitation:', error);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  // Function to load team details after successful registration
  const loadTeamDetails = async (teamId: string) => {
    try {
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('*')
        .eq('id', teamId)
        .single();

      if (teamError) {
        console.error('Error loading team details after registration:', teamError);
        return null;
      }

      return team;
    } catch (error) {
      console.error('Error in loadTeamDetails:', error);
      return null;
    }
  };

  const buildEmailRedirectTo = () => {
    const baseRedirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || `${window.location.origin}/auth/callback`;
    const url = new URL(baseRedirectUrl);
    if (invitationId) {
      url.searchParams.set('invitation', invitationId);
    }
    return url.toString();
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    if (!agreeToTerms) {
      setError('You must read and agree to the Terms and Conditions and the Safety & AI Disclaimer to create an account.');
      setLoading(false);
      return;
    }

    try {
      // Sign up with explicit redirect so email confirmation returns through AuthCallback.
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: buildEmailRedirectTo(),
        },
      });
      if (signUpError) throw signUpError;
      
      // If there's an invitation, process it after successful registration
      if (invitation) {
        // In environments with email confirmation enabled, the user won't have an active
        // session yet. Keep them on this page with a clear next-step message.
        if (!data.session || !data.user) {
          setSuccess(
            `Account created. Check your email to confirm your account, then use that link to finish joining ${invitation.team?.name}.`
          );
          toast.success('Account created. Check your email to confirm and complete team join.');
          return;
        }

        try {
          // Wait a moment for the user to be fully created
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Get the current user
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const result = await acceptCoachInvitation(invitation, user);

            if (!result.success) {
              toast.error('Account created and invitation accepted, but there was an issue with team membership. Please contact support.');
            } else {
              const team = await loadTeamDetails(invitation.team_id);
              toast.success(`Successfully joined ${team?.name || 'the team'}!`);
            }

            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('force-team-refresh'));
            }, 500);
            navigate(`/team/${invitation.team_id}`);
            return;
          }
        } catch (inviteError) {
          console.error('Error processing invitation:', inviteError);
          toast.error('Account created but there was an issue processing your invitation. Please contact the team admin.');
        }
      }
      
      // Wait a moment for everything to settle before navigation
      // Only navigate to home if there was no invitation (invitation cases navigate to team page)
      if (!invitation) {
        if (!data.session || !data.user) {
          setSuccess('Account created. Please check your email and click the confirmation link to finish sign in.');
          toast.success('Account created. Check your email to confirm your account.');
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate('/');
      }
    } catch (error: any) {
      const msg: string = error.message ?? '';
      if (
        msg.toLowerCase().includes('already registered') ||
        msg.toLowerCase().includes('already been registered') ||
        msg.toLowerCase().includes('user already exists') ||
        error.status === 422
      ) {
        // The email already has an account — send them to login to accept the invite there.
        toast('You already have an account. Please sign in to accept the invitation.', { icon: 'ℹ️' });
        navigate(invitationId ? `/login?invitation=${invitationId}` : '/login');
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-[#3A3C3E] py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Logo className="h-16" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-white">
          {invitation ? `Join ${invitation.team?.name}` : 'Create your account'}
        </h2>
        {invitation ? (
          <p className="mt-2 text-center text-sm text-gray-300">
            Create your account to join as a {invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}{' '}
            &mdash; or{' '}
            <Link to={`/login?invitation=${invitationId}`} className="font-medium text-primary hover:text-primary-hover underline">
              sign in if you already have an account
            </Link>
          </p>
        ) : (
          <p className="mt-2 text-center text-sm text-gray-300">
            Or{' '}
            <Link to="/login" className="font-medium text-primary hover:text-primary-hover underline">
              sign in to your account
            </Link>
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-md rounded-xl sm:px-10">
          {invitation && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-800 rounded-xl p-3">
              <div className="font-medium">Coach Invitation</div>
              <div className="text-sm mt-1">
                Team: <strong>{invitation.team?.name}</strong><br />
                Role: <strong>{invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}</strong><br />
                Sport: <strong>{invitation.team?.sport}</strong>
              </div>
            </div>
          )}
          
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 rounded-xl p-3">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-800 rounded-xl p-3">
              {success}
            </div>
          )}
          
          <form className="space-y-6" onSubmit={handleRegister}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-dark">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!!invitation} // Disable email field if coming from invitation
                  className="w-full px-3 py-2 border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {invitation && (
                <p className="mt-1 text-xs text-gray-500">
                  Email is pre-filled from your invitation
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-dark">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-stone rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                required
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-stone text-primary focus:ring-primary"
              />
              <label htmlFor="agreeToTerms" className="text-sm text-dark">
                I have read and agree to the{' '}
                <Link to="/legal/terms" className="text-primary underline hover:no-underline">
                  Terms and Conditions
                </Link>
                {' '}and the{' '}
                <Link to="/legal/safety-ai-disclaimer" className="text-primary underline hover:no-underline">
                  Safety &amp; AI Disclaimer
                </Link>
                .
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-cream py-3 px-4 rounded-xl text-lg font-bold hover:bg-primary-hover transition disabled:opacity-50"
              >
                {loading 
                  ? (invitation ? 'Creating account & joining team...' : 'Creating account...') 
                  : (invitation ? 'Create account & join team' : 'Create account')
                }
              </button>
            </div>
          </form>

          <SocialLogin 
            mode="register" 
            invitation={invitation} 
            className="mt-6" 
          />
          
          {!invitation && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary hover:text-primary-hover underline">
                  Sign in here
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;